import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { enablePush, isPushEnabledHere } from "../../lib/push";
import { pushReadiness, isIos, type PushReadiness } from "../../lib/platform";
import { Button, Card, Spinner } from "../../components/ui";
import { useToast } from "../../components/Toast";
import { errorMessage } from "../../lib/errors";

// Unchanged on purpose: renaming the key would re-show the card to everyone
// who already dismissed it.
const DISMISS_KEY = "lock-in:push-card-dismissed";

/**
 * Onboarding for notifications, which is where every PWA gets awkward:
 *
 *   Android/desktop  ask for permission whenever.
 *   iOS              the Push API does not exist in Safari at all. It appears
 *                    only after Add to Home Screen (iOS 16.4+), so asking is
 *                    impossible — the card has to teach the Share-sheet dance
 *                    first and only then offer the button.
 *
 * The design gives this one sentence and two buttons, so the Share-sheet steps
 * stay folded away behind "show me" until someone asks for them.
 */
export function NotificationsCard() {
  const { userId } = useAuth();
  const toast = useToast();

  const [readiness, setReadiness] = useState<PushReadiness>(() =>
    pushReadiness(),
  );
  const [enabledHere, setEnabledHere] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "1",
  );

  useEffect(() => {
    void isPushEnabledHere().then(setEnabledHere);
  }, [readiness.state]);

  // Recheck when the app is reopened — the user may have installed it since.
  useEffect(() => {
    const recheck = () => setReadiness(pushReadiness());
    document.addEventListener("visibilitychange", recheck);
    return () => document.removeEventListener("visibilitychange", recheck);
  }, []);

  async function handleEnable() {
    if (!userId) return;
    setBusy(true);
    try {
      const granted = await enablePush(userId);
      setEnabledHere(granted);
      setReadiness(pushReadiness());
      toast(
        granted
          ? "Notifications on 🔔"
          : "Notifications stay off — you can turn them on later",
        granted ? "success" : "info",
      );
    } catch (error) {
      toast(errorMessage(error, "Could not enable notifications"), "error");
    } finally {
      setBusy(false);
    }
  }

  // Nothing to say once this device is subscribed.
  if (enabledHere) return null;
  if (readiness.state === "unsupported") return null;
  if (dismissed && readiness.state !== "needs-install") return null;
  if (enabledHere === null) return null;

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }

  if (readiness.state === "needs-install") {
    return (
      <Prompt
        line="Add Our Pact to your home screen, then turn notifications on. That's the whole setup."
        confirmLabel={showSteps ? "got it" : "show me"}
        onConfirm={() => (showSteps ? dismiss() : setShowSteps(true))}
        onDismiss={dismiss}
      >
        {showSteps && <InstallSteps ios={isIos()} />}
      </Prompt>
    );
  }

  if (readiness.state === "denied") {
    return (
      <Prompt
        line="Notifications are blocked for this app. Turn them back on in your browser's site settings, then reopen Our Pact."
        confirmLabel="dismiss"
        onConfirm={dismiss}
      />
    );
  }

  return (
    <Prompt
      line="A ping when someone posts, a poke when someone is watching, and an evening reminder if you haven't checked in."
      confirmLabel={busy ? <Spinner className="border-t-current" /> : "turn on"}
      onConfirm={handleEnable}
      onDismiss={dismiss}
      busy={busy}
    />
  );
}

// ---------------------------------------------------------------------------

/** The card shape from the design: a sentence, then ghost and primary. */
function Prompt({
  line,
  confirmLabel,
  onConfirm,
  onDismiss,
  busy,
  children,
}: {
  line: string;
  confirmLabel: React.ReactNode;
  onConfirm: () => void;
  onDismiss?: () => void;
  busy?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <Card className="elev-md mb-4 gap-3 p-4.5">
      <p className="text-sm font-medium leading-relaxed">{line}</p>
      {children}
      <div className="flex justify-end gap-2.5">
        {onDismiss && (
          <Button variant="ghost" onClick={onDismiss}>
            not now
          </Button>
        )}
        <Button onClick={onConfirm} disabled={busy}>
          {confirmLabel}
        </Button>
      </div>
    </Card>
  );
}

/**
 * iOS only in practice. There is no beforeinstallprompt on Safari, so the only
 * honest option is to describe the Share sheet.
 */
function InstallSteps({ ios }: { ios: boolean }) {
  return (
    <ol className="flex flex-col gap-2 text-[13px] opacity-75">
      <li className="flex gap-2.5">
        <Step>1</Step>
        <span>
          {ios ? (
            <>
              Tap the <ShareGlyph /> Share button in Safari's toolbar
            </>
          ) : (
            <>Open your browser's menu</>
          )}
        </span>
      </li>
      <li className="flex gap-2.5">
        <Step>2</Step>
        <span>
          Choose <strong className="font-semibold">Add to Home Screen</strong>
        </span>
      </li>
      <li className="flex gap-2.5">
        <Step>3</Step>
        <span>Open Our Pact from your home screen and come back to this card</span>
      </li>
    </ol>
  );
}

function Step({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-accent-100 text-[11px] font-semibold text-accent-800">
      {children}
    </span>
  );
}

function ShareGlyph() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="inline size-4 -translate-y-px"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M10 2.5v10M10 2.5 7 5.5M10 2.5l3 3" />
      <path d="M5.5 8.5h-1v9h11v-9h-1" />
    </svg>
  );
}
